import type {
  CoverageScope,
  ServiceRange,
  SpecialtyCoverageItem,
  SpecialtyCoverageState,
} from './types';

/**
 * Normalize a GET item into a saveable editor row.
 * `coverageScope: null` (unconfigured — pre-config, or attached via
 * create-by-specialtyIds) stays null: the editor never fills it in by
 * inherit or default (issue 15/24). Save is blocked by the form schema
 * until the admin explicitly picks scope + at least one delivery mode.
 */
export function normalizeEditorSpecialty(item: {
  specialtyId: string;
  coverageScope: CoverageScope | null;
  serviceRanges: ServiceRange[];
  coverageStates: SpecialtyCoverageState[];
}): SpecialtyCoverageItem {
  return {
    specialtyId: item.specialtyId,
    coverageScope: item.coverageScope,
    serviceRanges: [...item.serviceRanges],
    coverageStates: item.coverageStates.map((state) => ({
      stateCode: state.stateCode,
      allCounties: state.allCounties,
      countyIds: state.allCounties ? [] : [...(state.countyIds ?? [])],
    })),
    isPersisted: true,
  };
}

/**
 * Strip UI-only fields for PATCH body / dirty-check comparison. Also called
 * with not-yet-configured rows (e.g. `SpecialtyCoverageSection`'s isDirty
 * check runs on every edit, before the form schema has validated the row) —
 * `coverageScope` can legitimately be `null` here, so the return type says
 * so instead of asserting it away. Actual submission is still blocked by the
 * form schema until every row has a non-null scope (issue 15/24).
 */
export function toSpecialtyCoveragePayload(
  items: SpecialtyCoverageItem[],
): Array<{
  specialtyId: string;
  coverageScope: CoverageScope | null;
  serviceRanges: ServiceRange[];
  coverageStates: SpecialtyCoverageState[];
}> {
  return items.map(({ specialtyId, coverageScope, serviceRanges, coverageStates }) => ({
    specialtyId,
    coverageScope,
    serviceRanges,
    coverageStates: coverageStates.map((state) => ({
      stateCode: state.stateCode,
      allCounties: state.allCounties,
      countyIds: state.allCounties ? [] : [...state.countyIds],
    })),
  }));
}

/**
 * PATCH-body builder: same shape as {@link toSpecialtyCoveragePayload}, but
 * asserts every row has a non-null `coverageScope` — the form schema blocks
 * submission until that's true, so this only throws on a real bug (a save
 * path that bypassed validation), never on a legitimate unconfigured row.
 */
export function toConfiguredCoveragePayload(
  items: SpecialtyCoverageItem[],
): Array<{
  specialtyId: string;
  coverageScope: CoverageScope;
  serviceRanges: ServiceRange[];
  coverageStates: SpecialtyCoverageState[];
}> {
  return toSpecialtyCoveragePayload(items).map((item) => {
    if (item.coverageScope === null) {
      throw new Error(
        `Cannot submit specialty ${item.specialtyId}: coverageScope is unconfigured`,
      );
    }
    return { ...item, coverageScope: item.coverageScope };
  });
}
